import { useQuery } from '@tanstack/react-query'
import { useRestApi } from '@wove/api'
import { toast } from 'sonner'

export interface CustomField {
  id: string
  field_key: string
  field_name: string
  field_type: 'text' | 'number' | 'checkbox' | 'date' | 'datetime'
  applies_to: 'rfq' | 'shipment' | 'container'
  is_ai_extractable: boolean
}

export function useCustomFields() {
  const api = useRestApi()

  return useQuery({
    queryKey: ['custom-fields'],
    queryFn: async () => {
      try {
        // First, we need to get the integrations to find the custom fields endpoint
        const integrationsResponse = await api.get('/api/v1/integrations')
        const integrations = Array.isArray(integrationsResponse.data)
          ? integrationsResponse.data
          : integrationsResponse.data.data || []

        if (!integrations || integrations.length === 0) {
          return []
        }

        // Get custom fields from all integrations
        const allCustomFields: CustomField[] = []

        for (const integration of integrations) {
          try {
            const response = await api.get(`/api/v1/integrations/${integration.id}/custom-fields`)
            const fields = response.data.data || []

            // Map the fields to our interface
            const mappedFields = fields.map((field: any) => ({
              id: field.id,
              field_key: field.fieldKey,
              field_name: field.fieldName,
              field_type: field.fieldType,
              applies_to: field.appliesTo,
              is_ai_extractable: field.isAiExtractable,
            }))

            allCustomFields.push(...mappedFields)
          } catch (error) {
            toast.error(`Failed to fetch custom fields for integration ${integration.id}`)
          }
        }

        return allCustomFields
      } catch (error) {
        return []
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}
